// 승객 본인 사원증 읽기 — 네이티브 플러그인 / Web NFC 분기 (2026-10-11 · 설계 §1·§2)
// Firebase import 금지(판정·등록은 호출부가 CF registerNfcCard 로).
//
// 분기 순서(QR 스캐너 `hasNativePlugin("CapacitorBarcodeScanner")` 와 같은 패턴):
//   ① 앱이고 `CapacitorNfc` 플러그인이 있으면 → 네이티브(안드로이드·iOS 모두)
//   ② 아니고 `NDEFReader` 가 있으면 → Web NFC(안드로이드 크롬만)
//   ③ 둘 다 없으면 → "none"(화면은 «앱에서 등록할 수 있습니다» 안내)
// 🔴 브라우저·PWA·플러그인 없는 옛 앱 빌드는 ①을 절대 타지 않는다(hasNativePlugin 이 false).
// 🔴 UID 는 **받은 모양 그대로** 돌려준다(네이티브 = 바이트 배열 · Web = "aa:bb.." 문자열).
//    hex 정규화는 서버 `functions/nfcUid.js` 한 곳뿐이다 — 여기서 변환하지 말 것.
import { hasNativePlugin, nativeCall, nativeListen } from "./nativeApp";
import { isWebNfcSupported } from "./nfc";

export const NFC_PLUGIN = "CapacitorNfc"; // @capgo/capacitor-nfc 의 registerPlugin 이름(소스 실측)

/** "native" | "web" | "none" */
export function detectNfcMode() {
  if (hasNativePlugin(NFC_PLUGIN)) return "native";
  if (isWebNfcSupported()) return "web";
  return "none";
}

function isIos() {
  try { return !!(window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() === "ios"); } catch { return false; }
}

/**
 * 카드 1장을 기다린다. onUid(raw) 는 카드를 읽을 때마다(호출부가 첫 장만 쓰고 stop 할 것).
 * @returns {Promise<() => void>} stop 함수
 */
export async function startCardScan({ onUid, onError, onEnd }) {
  const mode = detectNfcMode();
  if (mode === "native") {
    let ended = false;
    const subs = [];
    const stop = () => {
      if (ended) return; ended = true;
      subs.forEach((s) => s && s.remove());
      nativeCall(NFC_PLUGIN, "stopScanning").catch(() => {});
    };
    // nfcEvent = 태그 종류(tag·ndef…)와 무관하게 모두 온다 — tagDiscovered 만 들으면
    // NDEF 가 기록된 카드가 «안 읽힌다» 가 된다(안드로이드는 ndef 이벤트로 분류).
    subs.push(nativeListen(NFC_PLUGIN, "nfcEvent", (e) => {
      const id = e && e.tag && e.tag.id;
      if (Array.isArray(id) && id.length) onUid && onUid(id);
      else onError && onError(new Error("카드 번호를 읽지 못했습니다. 다시 대 주세요"));
    }));
    // iOS 는 시스템 시트를 사용자가 닫거나 시간이 지나면 세션이 끝난다.
    subs.push(nativeListen(NFC_PLUGIN, "nfcSessionEnd", (e) => { if (!ended) onEnd && onEnd(e && e.reason); }));
    try {
      // 🔴 iOS 는 `tag` 세션이어야 UID 가 온다(ndef 세션은 내용만 준다).
      await nativeCall(NFC_PLUGIN, "startScanning", isIos()
        ? { iosSessionType: "tag", invalidateAfterFirstRead: true, alertMessage: "사원증을 휴대폰 윗부분에 대 주세요" }
        : {});
    } catch (e) {
      stop();
      throw e;
    }
    return stop;
  }
  if (mode === "web") {
    const ctrl = new AbortController();
    // eslint-disable-next-line no-undef
    const reader = new window.NDEFReader();
    reader.onreading = (ev) => { if (ev && ev.serialNumber) onUid && onUid(ev.serialNumber); };
    reader.onreadingerror = () => onError && onError(new Error("카드를 읽지 못했습니다. 다시 대 주세요"));
    await reader.scan({ signal: ctrl.signal });
    return () => { try { ctrl.abort(); } catch (_) { /* 무시 */ } };
  }
  throw new Error("이 기기에서는 카드를 읽을 수 없습니다");
}

/** 네이티브 NFC 상태(꺼짐 안내용). 실패하면 null. */
export async function nativeNfcStatus() {
  try { const r = await nativeCall(NFC_PLUGIN, "getStatus"); return r && r.status; } catch { return null; }
}
